import i18n from 'src/locales';
import {
  ErrorLogChannel,
  ErrorQueryEvent,
  Severity,
} from 'src/types/ErrorEventTypes';
import { AllConversationChannel } from 'src/types/workflowBuilderAPITypes';
import { Routes } from 'src/utils/enums';

// This module is imported across surfaces (e.g. workflow-builder-edit), so it
// binds its own namespaced `t` from the shared i18n instance rather than taking
// a `t` param bound to a caller's namespace.
const t = i18n.getFixedT(null, 'events');

export const MISSING_CONTEXT_VARIABLE = 'Missing Context Variable';

export const CONTEXT_VARIABLE_TYPE_MISMATCH = 'Context Variable type mismatch';

export const NO_VALID_CONDITION = 'No Valid Condition';

export const ZENDESK_REQUESTER_EMAIL_SUSPENDED =
  'Zendesk: requester email is suspended';

export const ZENDESK_REQUESTER_EMAIL_INVALID =
  'Zendesk: requester email is of invalid format';

export const ZENDESK_REQUESTER_EMAIL_IS_SUPPORT_ADDRESS =
  'Zendesk: requester email is a support address and cannot be used';

export const ZENDESK_VALUE_INVALID_FORMAT =
  'Zendesk: provided value is of invalid format';

export const CANNOT_INSERT_UPDATE_ACTIVATE_ENTITY =
  'Salesforce: case entity operation error for insert or update';

export const INSUFFICIENT_ACCESS_ON_CROSS_REFERENCE_ENTITY =
  'Salesforce: Insufficient Access on Cross Reference Entity';

export const MALFORMED_ID = 'Salesforce: invalid or malformed id';

export const INVALID_CROSS_REFERENCE_KEY = 'Salesforce: invalid reference key';

export const FIELD_INTEGRITY_EXCEPTION =
  'Salesforce: field integrity exception';
export const SALESFORCE_UPLOAD_ATTACHMENT_ERROR =
  'Failed to upload attachment to Salesforce';
export const SALESFORCE_MESSAGING_WIDGET_NOT_FOUND_ERROR =
  'Salesforce Messaging Widget not found';
export const ZENDESK_MESSAGING_WIDGET_NOT_FOUND_ERROR =
  'Zendesk Messaging Widget not found';
export const FRESHCHAT_WIDGET_NOT_FOUND_ERROR = 'Freshchat Widget not found';
export const SALESFORCE_CHAT_BUTTON_NOT_FOUND_ERROR =
  'Salesforce Chat Button not found';
export const SALESFORCE_WIDGET_NOT_FOUND_ERROR = 'Salesforce Widget not found';
export const SALESFORCE_ONE_CHAT_HANDOFF_ERROR =
  'Salesforce One Chat handoff error';
export const ZENDESK_WIDGET_NOT_FOUND_ERROR = 'Zendesk Widget not found';
export const KUSTOMER_WIDGET_NOT_FOUND_ERROR = 'Kustomer Widget not found';
export const KUSTOMER_CONVERSATION_ERROR =
  'Failed to create Kustomer conversation';
export const SNAPENGAGE_WIDGET_NOT_FOUND_ERROR = 'SnapEngage Widget not found';
export const FAILED_TO_DECODE_JWT = 'Failed to decode JWT';
export const CV_NAME_NOT_FOUND_IN_DECODED_JWT =
  'Context Variable name not found in decoded JWT';
export const CONTEXT_VARIABLE_NOT_FOUND_WHILE_STORING_DECODED_JWT =
  'Context Variable not found during decoding and storage of JWT';
export const JIRA_SERVICE_MANAGEMENT_REQUEST_CREATION_FAILED =
  'Jira Service Management request creation failed';

export const JMESPATH_QUERY_EVALUATION_FAILED =
  'JMESPath query evaluation failed';

export const MCP_TOOL_CALL_FAILED = 'MCP tool call failed';
export const CODE_EXECUTION_FAILED = 'Code execution failed';
//assist
export const TICKET_FETCH_FAILED = 'Ticket fetch failed';
//solve email
export const WEBHOOK_REQUEST_VALIDATION_ERROR =
  'Webhook request validation error';
export const EMAIL_CONVERSATION_MISSING_CONTEXT_VARIABLES =
  'Missing Context Variables';
export const EMAIL_CONVERSATION_MISSING_STATIC_ARTICLE =
  'Missing Static Article';
export const TICKET_UPDATE_STEP_ERROR = 'Ticket Update Step Error';
export const MISSING_ZENDESK_TICKET_FIELD = 'Zendesk: Missing Ticket Field';
export const ZENDESK_API_ERROR = 'Zendesk: API Error';
export const EMAIL_CONVERSATION_MISSING_STATIC_ARTICLE_TRANSLATION =
  'Missing Static Article Translation';
export const HALOCRM_API_ERROR = 'HaloCRM: API Error';

//Widget Error type constants
const ADMIN_CONFIGURATION = 'ADMIN_CONFIGURATION';
const ZENDESK_CONFIGURATION = 'ZENDESK_CONFIGURATION';
const SALESFORCE_CONFIGURATION = 'SALESFORCE_CONFIGURATION';
const HALOCRM_CONFIGURATION = 'HALOCRM_CONFIGURATION';
const JIRA_SERVICE_MANAGEMENT_CONFIGURATION =
  'JIRA_SERVICE_MANAGEMENT_CONFIGURATION';
const FRESHDESK_CONFIGURATION = 'FRESHDESK_CONFIGURATION';
const HUBSPOT_CONFIGURATION = 'HUBSPOT_CONFIGURATION';
const INTERCOM_CONFIGURATION = 'INTERCOM_CONFIGURATION';
const GORGIAS_CONFIGURATION = 'GORGIAS_CONFIGURATION';
const FRONT_CONFIGURATION = 'FRONT_CONFIGURATION';
const ZAMMAD_CONFIGURATION = 'ZAMMAD_CONFIGURATION';
const PYLON_CONFIGURATION = 'PYLON_CONFIGURATION';
const KUSTOMER_CONFIGURATION = 'KUSTOMER_CONFIGURATION';
const DYNAMICS_365_CONFIGURATION = 'DYNAMICS_365_CONFIGURATION';
const JIRA_CONFIGURATION = 'JIRA_CONFIGURATION';
const JMESPATH = 'JMESPATH';
//Email Error type constants
const MISSING_ZENDESK_TICKET_FIELD_EMAIL_ERROR = 'MISSING_ZENDESK_TICKET_FIELD';
const ZENDESK_API_EMAIL_ERROR = 'ZENDESK_API_ERROR';

export const INTEGRATIONS_PRODUCT = 'Integrations';
export const TRIAGE_PRODUCT = 'Triage';

//Triage helpdesk write rejections — these strings must stay in sync with
//TriageErrorMessage in forethought-server; they are the raw error_message
//values the backend writes to the error events table.
export const TRIAGE_RESTRICTED_PICKLIST_VALUE_REJECTED =
  'Predicted value is not an allowed option for the field';
export const TRIAGE_FIELD_TYPE_MISMATCH =
  "Predicted value does not match the field's type";
export const TRIAGE_FIELD_VALIDATION_RULE_REJECTED =
  'Predicted value was rejected by a field validation rule';
export const TRIAGE_FIELD_NOT_WRITABLE =
  'Field is read-only or cannot be updated by Forethought';
export const TRIAGE_HELPDESK_REJECTED_VALUE =
  'Helpdesk rejected the predicted value';
export const TRIAGE_TICKET_CLOSED_FOR_UPDATES =
  'Ticket was closed before Triage could update it';
export const TRIAGE_REQUIRED_FIELD_MISSING =
  'Another required field on the ticket blocked the update';
export const TRIAGE_VALUE_NOT_A_CONFIGURED_OPTION =
  "Predicted value did not match the field's configured options";
export const TRIAGE_OUTPUT_VALUE_CAST_FAILED =
  'Predicted value could not be converted to the type set for the field';
export const TRIAGE_TICKET_FETCH_FAILED =
  'Ticket could not be read from the helpdesk';

//Connector error constants
const CONNECTOR_API_ERROR_PREFIX = 'Integration returned error code';
const CONNECTOR_TEMPORARY_ERROR = 'Temporary processing error';
const CONNECTOR_PROCESSING_ERROR = 'Processing error';

export const resolveErrorDescription = (
  errorMessage: string,
  product: string,
) => {
  if (product === 'Solve Widget') {
    return getSolveWidgetErrorDescriptionByMessage(errorMessage);
  }
  if (product === 'Solve Email') {
    return getSolveEmailErrorDescriptionByMessage(errorMessage);
  }
  if (product === INTEGRATIONS_PRODUCT) {
    return getIntegrationsErrorDescriptionByMessage(errorMessage);
  }
  if (product === 'Assist') {
    return getAssistErrorDescriptionByMessage(errorMessage);
  }
  if (product === TRIAGE_PRODUCT) {
    return getTriageErrorDescriptionByMessage(errorMessage);
  }
};

export const getTriageErrorDescriptionByMessage = (errorMessage: string) => {
  switch (errorMessage) {
    case TRIAGE_RESTRICTED_PICKLIST_VALUE_REJECTED:
      return t('utils.triageDescription.restrictedPicklistValueRejected');
    case TRIAGE_FIELD_TYPE_MISMATCH:
      return t('utils.triageDescription.fieldTypeMismatch');
    case TRIAGE_FIELD_VALIDATION_RULE_REJECTED:
      return t('utils.triageDescription.fieldValidationRuleRejected');
    case TRIAGE_FIELD_NOT_WRITABLE:
      return t('utils.triageDescription.fieldNotWritable');
    case TRIAGE_HELPDESK_REJECTED_VALUE:
      return t('utils.triageDescription.helpdeskRejectedValue');
    case TRIAGE_TICKET_CLOSED_FOR_UPDATES:
      return t('utils.triageDescription.ticketClosedForUpdates');
    case TRIAGE_REQUIRED_FIELD_MISSING:
      return t('utils.triageDescription.requiredFieldMissing');
    case TRIAGE_VALUE_NOT_A_CONFIGURED_OPTION:
      return t('utils.triageDescription.valueNotAConfiguredOption');
    case TRIAGE_OUTPUT_VALUE_CAST_FAILED:
      return t('utils.triageDescription.outputValueCastFailed');
    case TRIAGE_TICKET_FETCH_FAILED:
      return t('utils.triageDescription.ticketFetchFailed');
    default:
      return t('errorDrawer.notApplicable');
  }
};

export const getSolveWidgetErrorDescriptionByMessage = (
  errorMessage: string,
) => {
  switch (errorMessage) {
    case SALESFORCE_UPLOAD_ATTACHMENT_ERROR:
      return t('utils.solveWidgetDescription.salesforceUploadAttachmentError');
    case MISSING_CONTEXT_VARIABLE:
      return t('utils.solveWidgetDescription.missingContextVariable');
    case CONTEXT_VARIABLE_TYPE_MISMATCH:
      return t('utils.solveWidgetDescription.contextVariableTypeMismatch');
    case NO_VALID_CONDITION:
      return t('utils.solveWidgetDescription.noValidCondition');
    case ZENDESK_REQUESTER_EMAIL_INVALID:
      return t('utils.solveWidgetDescription.zendeskRequesterEmailInvalid');
    case ZENDESK_REQUESTER_EMAIL_IS_SUPPORT_ADDRESS:
      return t(
        'utils.solveWidgetDescription.zendeskRequesterEmailIsSupportAddress',
      );
    case ZENDESK_REQUESTER_EMAIL_SUSPENDED:
      return t('utils.solveWidgetDescription.zendeskRequesterEmailSuspended');
    case ZENDESK_VALUE_INVALID_FORMAT:
      return t('utils.solveWidgetDescription.zendeskValueInvalidFormat');
    case CANNOT_INSERT_UPDATE_ACTIVATE_ENTITY:
      return t('utils.solveWidgetDescription.cannotInsertUpdateActivateEntity');
    case INSUFFICIENT_ACCESS_ON_CROSS_REFERENCE_ENTITY:
      return t(
        'utils.solveWidgetDescription.insufficientAccessOnCrossReferenceEntity',
      );
    case MALFORMED_ID:
      return t('utils.solveWidgetDescription.malformedId');
    case INVALID_CROSS_REFERENCE_KEY:
      return t('utils.solveWidgetDescription.invalidCrossReferenceKey');
    case FIELD_INTEGRITY_EXCEPTION:
      return t('utils.solveWidgetDescription.fieldIntegrityException');
    case SALESFORCE_MESSAGING_WIDGET_NOT_FOUND_ERROR:
      return getIntegrationWidgetType('Salesforce Messaging Widget');
    case ZENDESK_MESSAGING_WIDGET_NOT_FOUND_ERROR:
      return getIntegrationWidgetType('Zendesk Messaging Widget');
    case FRESHCHAT_WIDGET_NOT_FOUND_ERROR:
      return getIntegrationWidgetType('Freshchat Widget');
    case SALESFORCE_CHAT_BUTTON_NOT_FOUND_ERROR:
    case SALESFORCE_WIDGET_NOT_FOUND_ERROR:
      return getIntegrationWidgetType('Salesforce Widget');
    case SALESFORCE_ONE_CHAT_HANDOFF_ERROR:
      return t('utils.solveWidgetDescription.salesforceOneChatHandoffError', {
        handoffMessage: t('utils.handoffUnsuccessfulBranchMessage'),
      });
    case ZENDESK_WIDGET_NOT_FOUND_ERROR:
      return getIntegrationWidgetType('Zendesk Widget');
    case KUSTOMER_WIDGET_NOT_FOUND_ERROR:
      return getIntegrationWidgetType('Kustomer Widget');
    case KUSTOMER_CONVERSATION_ERROR:
      return t('utils.solveWidgetDescription.kustomerConversationError', {
        handoffMessage: t('utils.handoffUnsuccessfulBranchMessage'),
      });
    case SNAPENGAGE_WIDGET_NOT_FOUND_ERROR:
      return getIntegrationWidgetType('SnapEngage Widget');
    case FAILED_TO_DECODE_JWT:
      return t('utils.solveWidgetDescription.failedToDecodeJwt');
    case CV_NAME_NOT_FOUND_IN_DECODED_JWT:
      return t('utils.solveWidgetDescription.cvNameNotFoundInDecodedJwt');
    case CONTEXT_VARIABLE_NOT_FOUND_WHILE_STORING_DECODED_JWT:
      return t(
        'utils.solveWidgetDescription.contextVariableNotFoundWhileStoringDecodedJwt',
      );
    case HALOCRM_API_ERROR:
      return t('utils.solveWidgetDescription.halocrmApiError');
    case MCP_TOOL_CALL_FAILED:
      return t('utils.solveWidgetDescription.mcpToolCallFailed');
    case JIRA_SERVICE_MANAGEMENT_REQUEST_CREATION_FAILED:
      return t(
        'utils.solveWidgetDescription.jiraServiceManagementRequestCreationFailed',
      );
    case JMESPATH_QUERY_EVALUATION_FAILED:
      return t('utils.solveWidgetDescription.jmespathQueryEvaluationFailed');
    default:
      return t('errorDrawer.notApplicable');
  }
};

export const getSolveEmailErrorDescriptionByMessage = (
  errorMessage: string,
) => {
  switch (errorMessage) {
    case WEBHOOK_REQUEST_VALIDATION_ERROR:
      return t('utils.solveEmailDescription.webhookRequestValidationError');
    case EMAIL_CONVERSATION_MISSING_CONTEXT_VARIABLES:
      return t('utils.solveEmailDescription.emailMissingContextVariables');
    case EMAIL_CONVERSATION_MISSING_STATIC_ARTICLE:
      return t('utils.solveEmailDescription.emailMissingStaticArticle');
    case EMAIL_CONVERSATION_MISSING_STATIC_ARTICLE_TRANSLATION:
      return t(
        'utils.solveEmailDescription.emailMissingStaticArticleTranslation',
      );
    case TICKET_UPDATE_STEP_ERROR:
      return t('utils.solveEmailDescription.ticketUpdateStepError');
    case MISSING_ZENDESK_TICKET_FIELD:
      return t('utils.solveEmailDescription.missingZendeskTicketField');
    case ZENDESK_API_ERROR:
      return t('utils.solveEmailDescription.zendeskApiError');
    case HALOCRM_API_ERROR:
      return t('utils.solveEmailDescription.halocrmApiError');
    case JMESPATH_QUERY_EVALUATION_FAILED:
      return t('utils.solveEmailDescription.jmespathQueryEvaluationFailed');
    default:
      return t('errorDrawer.notApplicable');
  }
};

export const getChatUrl = ({
  channel,
  chatsTabIndex,
  conversationId,
}: {
  channel: ErrorLogChannel | null;
  chatsTabIndex: number;
  conversationId: string | null;
}) => {
  if (!conversationId || !channel) {
    return null;
  }

  // Triage identifiers are ticket / orchestrator run ids with no Solve
  // Insights conversation behind them. Triage's `api` channel is ambiguous
  // with Solve's headless channel, so callers must additionally skip this
  // for Triage rows.
  if (channel === 'helpdesk' || channel === 'orchestrator') {
    return null;
  }

  if ((channel === 'widget' || channel === 'sunshine') && chatsTabIndex >= 0) {
    return `${Routes.SOLVE_INSIGHTS_MAIN}?tab=${chatsTabIndex}&chats_search=${conversationId}`;
  }

  // Slack and Headless (api) conversations live on their own product mode in
  // Solve Insights — without the param the page falls back to Widget.
  if ((channel === 'slack' || channel === 'api') && chatsTabIndex >= 0) {
    return `${Routes.SOLVE_INSIGHTS_MAIN}?tab=${chatsTabIndex}&chats_search=${conversationId}&product_mode=${channel}`;
  }

  if (channel === 'playbook' && chatsTabIndex >= 0) {
    return `${Routes.ASSIST_INSIGHTS}?chats_search=${conversationId}`;
  }

  if (channel === 'live') {
    return chatsTabIndex >= 0
      ? `${Routes.SOLVE_INSIGHTS_MAIN}?tab=${chatsTabIndex}&chats_search=${conversationId}&product_mode=email`
      : null;
  }

  return `${Routes.SOLVE_INSIGHTS_MAIN}?tab=${chatsTabIndex}&chats_search=${conversationId}&product_mode=${channel}`;
};

export const toConversationChannel = (
  channel: ErrorLogChannel | null,
): AllConversationChannel | null => {
  if (channel === 'helpdesk' || channel === 'orchestrator') {
    return null;
  }
  return channel;
};

// Only widget conversations have a conversation drawer behind them; every
// other product keeps the event drawer.
export const isConversationDrawerEvent = ({
  channel,
  conversation_id,
  product,
}: Pick<ErrorQueryEvent, 'channel' | 'conversation_id' | 'product'>) =>
  Boolean(conversation_id) &&
  product === 'Solve Widget' &&
  (channel === 'widget' || channel === 'sunshine');

export const sortColumnsValidator = () => (sortColumn: string) => {
  const validColums = ['timestamp', 'status_code'];
  return validColums.includes(sortColumn);
};

export const getIntegrationWidgetType = (integrationWidgetType: string) => {
  return t('utils.integrationWidgetType', {
    handoffMessage: t('utils.handoffUnsuccessfulBranchMessage'),
    integrationWidgetType,
  });
};

export const getHowToFixHandoffMessage = (integrationWidgetType: string) => {
  return t('utils.howToFixHandoffMessage', { integrationWidgetType });
};

export const getIntegrationsErrorDescriptionByMessage = (
  errorMessage: string,
) => {
  if (errorMessage.startsWith(CONNECTOR_API_ERROR_PREFIX)) {
    return t('utils.connectorApiError');
  }
  if (errorMessage === CONNECTOR_TEMPORARY_ERROR) {
    return t('utils.connectorTemporaryError');
  }
  if (errorMessage === CONNECTOR_PROCESSING_ERROR) {
    return t('utils.connectorProcessingError');
  }
  return t('errorDrawer.notApplicable');
};

export const getAssistErrorDescriptionByMessage = (errorMessage: string) => {
  switch (errorMessage) {
    case MISSING_CONTEXT_VARIABLE:
      return t('utils.assistDescription.missingContextVariable');
    case TICKET_FETCH_FAILED:
      return t('utils.assistDescription.ticketFetchFailed');
    default:
      return t('errorDrawer.notApplicable');
  }
};

export const getUserImpactFromError = (error: ErrorQueryEvent) => {
  const product = error.product;

  if (product == 'Solve Widget') {
    return getSolveWidgetErrorUserImpact(error);
  }
  if (product == 'Solve Email') {
    return getSolveEmailErrorUserImpact(error);
  }
  if (product === INTEGRATIONS_PRODUCT) {
    return t('utils.userImpact.connectorDataSyncDelayed');
  }
  if (product === 'Assist') {
    return getAssistErrorUserImpact(error);
  }
  if (product === TRIAGE_PRODUCT) {
    return getTriageErrorUserImpact(error);
  }
  return t('errorDrawer.notApplicable');
};

const getTriageErrorUserImpact = (error: ErrorQueryEvent) => {
  // A ticket Triage can't read gets no predictions at all, rather than one
  // field left unset.
  if (error.error_message === TRIAGE_TICKET_FETCH_FAILED) {
    return t('utils.userImpact.triageNoPredictionsMade');
  }
  return t('utils.userImpact.triageFieldNotUpdated');
};

const getAssistErrorUserImpact = (error: ErrorQueryEvent) => {
  switch (error.error_message) {
    case MISSING_CONTEXT_VARIABLE:
      return t('utils.userImpact.chatInterrupted');
    case TICKET_FETCH_FAILED:
      return t('utils.userImpact.noRelevantTicketContextForAssist');
    default:
      return t('errorDrawer.notApplicable');
  }
};

export const getSolveWidgetErrorUserImpact = (error: ErrorQueryEvent) => {
  const { error_message, error_type } = error;
  const isAdminConfigError = error_type == ADMIN_CONFIGURATION;
  const isHelpdeskError =
    error_type == ZENDESK_CONFIGURATION ||
    error_type == SALESFORCE_CONFIGURATION ||
    error_type == HALOCRM_CONFIGURATION ||
    error_type == JIRA_SERVICE_MANAGEMENT_CONFIGURATION ||
    error_type == FRESHDESK_CONFIGURATION ||
    error_type == HUBSPOT_CONFIGURATION ||
    error_type == INTERCOM_CONFIGURATION ||
    error_type == GORGIAS_CONFIGURATION ||
    error_type == FRONT_CONFIGURATION ||
    error_type == ZAMMAD_CONFIGURATION ||
    error_type == PYLON_CONFIGURATION ||
    error_type == KUSTOMER_CONFIGURATION ||
    error_type == DYNAMICS_365_CONFIGURATION ||
    error_type == JIRA_CONFIGURATION;
  const isParseJwtError = getParseJWTErrors().includes(error_message);

  if (isHelpdeskError || (isAdminConfigError && !isParseJwtError)) {
    return t('utils.userImpact.chatInterrupted');
  }
  return t('errorDrawer.notApplicable');
};

const getParseJWTErrors = () => {
  return [
    FAILED_TO_DECODE_JWT,
    CV_NAME_NOT_FOUND_IN_DECODED_JWT,
    CONTEXT_VARIABLE_NOT_FOUND_WHILE_STORING_DECODED_JWT,
  ];
};

const getSolveEmailErrorUserImpact = (error: ErrorQueryEvent) => {
  const { error_payload, error_type } = error;

  const notApplicableErrors = [JMESPATH];

  if (notApplicableErrors.includes(error_type || '')) {
    return t('errorDrawer.notApplicable');
  }

  if (!getSolveEmailZendeskErrors().includes(error_type || '')) {
    return t('utils.userImpact.noEmailResponseSent');
  }

  const isTicketUpdateError =
    typeof error_payload === 'object' &&
    error_payload !== null &&
    'step_type' in error_payload &&
    error_payload.step_type === 'ticket_update';

  // If the error occurred during the ticket update step, the email response was already sent
  if (isTicketUpdateError) {
    return t('errorDrawer.notApplicable');
  }
};
const getSolveEmailZendeskErrors = () => {
  return [MISSING_ZENDESK_TICKET_FIELD_EMAIL_ERROR, ZENDESK_API_EMAIL_ERROR];
};

export const getErrorChannelLabel = (channel: string): string => {
  const channelMap: Record<string, string> = {
    api: t('utils.channelLabel.api'),
    helpdesk: t('utils.channelLabel.helpdesk'),
    intercom_sms: t('utils.channelLabel.intercomSms'),
    kustomer_sms: t('utils.channelLabel.kustomerSms'),
    live: t('utils.channelLabel.live'),
    orchestrator: t('utils.channelLabel.orchestrator'),
    playbook: t('utils.channelLabel.playbook'),
    sunshine: t('utils.channelLabel.sunshine'),
    widget: t('utils.channelLabel.widget'),
  };

  return (
    channelMap[channel] ??
    channel
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
};

export const getErrorProductLabel = (
  product: ErrorQueryEvent['product'],
): string => {
  switch (product) {
    case 'Solve Email':
    case 'Solve Widget':
      return t('utils.productLabel.solve');
    default:
      return product;
  }
};

export const isErrorLogSeverity = (value: string | null): value is Severity =>
  value === 'high' || value === 'medium' || value === 'low';

export type FilterOption = { label: string; value: string };
export type FilterGroup = {
  label: string;
  options: FilterOption[];
  value: string;
};

export const categorizeSelectedFilters = (
  multiSelected: string[],
  filterOptions: FilterGroup[],
) => {
  // Grouped by each group's own `value`, not its position: the product group
  // only appears for orgs with errors from more than one product, so severity
  // no longer sits at a fixed index.
  const selectedByGroup: Record<string, string[]> = {
    channel: [],
    error_message: [],
    product: [],
    severity: [],
  };

  multiSelected.forEach(item => {
    const group = filterOptions.find(({ options }) =>
      options.some(option => option.value === item),
    );
    const bucket = group ? selectedByGroup[group.value] : undefined;
    // Array check rather than `in`, which would also answer for inherited
    // Object keys if a group is ever named `toString` and friends.
    if (Array.isArray(bucket)) {
      bucket.push(item);
    }
  });

  return {
    channel: selectedByGroup.channel.join(','),
    errorMessage: selectedByGroup.error_message.join(','),
    // A single product option can carry several raw products (Solve Widget +
    // Solve Email behind "Solve"), so this is already a comma-joined CSV.
    product: selectedByGroup.product.join(','),
    severity: selectedByGroup.severity.join(','),
  };
};
